package us.laantech.common.ui

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import kotlinx.datetime.*
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Modal dialog that lets the user pick a date and time.
 *
 * @param initialEpochMillis Optional initial value (UTC milliseconds since epoch).
 *                           If null, the current selection in the date picker is used
 *                           and the time defaults to 00:00.
 * @param title              Title shown at the top of the dialog.
 * @param is24Hour           Whether the time picker should use 24-hour format.
 * @param onDismiss          Called when the user cancels or dismisses the dialog.
 * @param onConfirm          Called with the chosen UTC epoch milliseconds when confirmed.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Preview(widthDp = 400, heightDp = 700)
@Composable
fun DateTimeEntryModal(
    initialEpochMillis: Long? = Clock.System.now().toEpochMilliseconds(),
    title: String = "Select date & time",
    is24Hour: Boolean = false,
    onDismiss: () -> Unit = {},
    onConfirm: (epochMillis: Long) -> Unit = {},
) {
    val timeZone = remember { TimeZone.currentSystemDefault() }

    // Convert the incoming UTC epoch millis to the user's *local* wall-clock date/time.
    val initialLocal: LocalDateTime = remember(initialEpochMillis, timeZone) {
        val instant = initialEpochMillis
            ?.let { Instant.fromEpochMilliseconds(it) }
            ?: Clock.System.now()
        instant.toLocalDateTime(timeZone)
    }

    // --- Date state ---
    // DatePicker expects "UTC midnight of the selected day". We feed it the UTC midnight
    // that corresponds to the user's *local* date so the highlighted day matches what
    // the user expects in their time zone.
    val initialDateAsUtcMidnight: Long = remember(initialLocal) {
        initialLocal.date
            .atStartOfDayIn(TimeZone.UTC)
            .toEpochMilliseconds()
    }
    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = initialDateAsUtcMidnight,
    )

    // --- Time state ---
    val timePickerState = rememberTimePickerState(
        initialHour = initialLocal.hour,
        initialMinute = initialLocal.minute,
        is24Hour = is24Hour,
    )

    // --- Tabs (Date | Time) ---
    var selectedTab by remember { mutableStateOf(0) }
    val tabs = listOf("Date", "Time")

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            TabRow(selectedTabIndex = selectedTab) {
                tabs.forEachIndexed { index, label ->
                    Tab(
                        selected = selectedTab == index,
                        onClick = { selectedTab = index },
                        text = { Text(label) },
                    )
                }
            }
        },
        containerColor = Color.DarkGray,
        text = {
            Column(Modifier.fillMaxWidth()) {


                Spacer(Modifier.height(8.dp))

                when (selectedTab) {
                    0 -> DatePicker(
                        state = datePickerState,
                        modifier = Modifier.padding(top = 8.dp),
                        colors = DatePickerDefaults.colors().copy(
                            containerColor = Color.DarkGray,
                            titleContentColor = Color.White,
                            headlineContentColor = Color.White,
                            weekdayContentColor = Color.White,
                            navigationContentColor = Color.White,
                            yearContentColor = Color.White
                        )
                    )
                    1 -> Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 16.dp),
                        horizontalArrangement = Arrangement.Center,
                    ) {
                        TimePicker(state = timePickerState,
                            colors = TimePickerDefaults.colors().copy(
                                clockDialColor = Color.DarkGray
                            ))
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    val dateMillis = datePickerState.selectedDateMillis
                    if (dateMillis != null) {
                        // `dateMillis` is UTC midnight of the picked calendar day.
                        // Extract that calendar date, then combine with the time
                        // the user picked, interpreted in the *local* time zone,
                        // and convert the result back to a UTC epoch.
                        val pickedDate: LocalDate = Instant
                            .fromEpochMilliseconds(dateMillis)
                            .toLocalDateTime(TimeZone.UTC)
                            .date

                        val localDateTime = LocalDateTime(
                            year = pickedDate.year,
                            month = pickedDate.month,
                            day = pickedDate.day,
                            hour = timePickerState.hour,
                            minute = timePickerState.minute,
                            second = 0,
                            nanosecond = 0,
                        )

                        val utcEpochMillis = localDateTime
                            .toInstant(timeZone)
                            .toEpochMilliseconds()

                        onConfirm(utcEpochMillis)
                    }
                },
                enabled = datePickerState.selectedDateMillis != null,
            ) { Text("OK") }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel") }
        },
    )
}